import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle2, Loader2, AlertCircle, ArrowRight, Mail } from "lucide-react";
import { apiClient } from "../lib/api";

/**
 * Magic-link landing page. Hits POST /api/verify/:token to flip the
 * email_verified flag, then surfaces a button to open the report (with
 * downloads now enabled).
 */
export default function VerifyEmail() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState("verifying"); // verifying | ok | error
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await apiClient.post(`/verify/${token}`);
        if (!alive) return;
        setData(r.data);
        setState("ok");
      } catch (err) {
        if (!alive) return;
        setError(err?.response?.data?.detail || "Invalid or expired verification link");
        setState("error");
      }
    })();
    return () => {
      alive = false;
    };
  }, [token]);

  return (
    <section className="min-h-[70vh] bg-[#0A0A0A] text-white" data-testid="verify-email-page">
      <div className="max-w-[720px] mx-auto px-6 lg:px-12 py-20">
        <span className="font-mono text-[11px] uppercase tracking-widest text-[#0047FF]">
          / Verify email
        </span>

        {state === "verifying" && (
          <div className="mt-6" data-testid="verify-loading">
            <div className="flex items-center gap-3">
              <Loader2 className="w-6 h-6 animate-spin text-[#0047FF]" />
              <span className="font-mono text-xs uppercase tracking-widest text-white/60">
                Verifying your email…
              </span>
            </div>
          </div>
        )}

        {state === "ok" && data && (
          <div className="mt-6" data-testid="verify-success">
            <div className="inline-flex items-center gap-2 bg-[#10B981] text-white px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest">
              <CheckCircle2 className="w-3.5 h-3.5" /> Email verified
            </div>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.02] mt-4">
              You're in.
            </h1>
            <p className="text-white/70 mt-4 max-w-xl leading-relaxed">
              Email <span className="text-white font-mono">{data.email}</span> is now
              verified for <span className="text-white font-mono">{data.domain}</span>.
              In-app PDF downloads are unlocked on your report.
            </p>
            <button
              onClick={() => navigate(`/report/${data.domain}`)}
              className="mt-8 pl-btn-primary px-6 py-3.5 border border-white inline-flex items-center gap-2"
              data-testid="open-report-button"
            >
              Open your report
              <ArrowRight className="w-4 h-4" />
            </button>
            <p className="mt-6 text-xs text-white/50 font-mono uppercase tracking-widest flex items-center gap-2">
              <Mail className="w-3.5 h-3.5" />
              The PDFs are also attached to your delivery email.
            </p>
          </div>
        )}

        {state === "error" && (
          <div className="mt-6" data-testid="verify-error">
            <div className="border border-[#EF4444] bg-[#EF4444]/10 text-[#FCA5A5] p-4 flex gap-3">
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-medium text-white">Could not verify</div>
                <p className="text-sm mt-1">{error}</p>
              </div>
            </div>
            <Link
              to="/"
              className="mt-6 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-white/70 hover:text-white"
              data-testid="back-home-link"
            >
              ← Back to home
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
